import { FormProvider } from "react-hook-form";
import {
  AlertDialog,
  AlertDialogTrigger,
} from "@/view/components/ui/alert-dialog";
import { Card } from "@/view/components/ui/card";
import { Spinner } from "@/view/components/ui/spinner";
import ButtonBack from "@/view/components/ButtonBack";
import { PartnerType } from "@/app/utils/partners.enum";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { useProductVisibility } from "@/app/hooks/useProductVisibility";
import { TriStateEnum } from "@/app/entities/TriState";
import { usePartnersPageController } from "./usePartnersPageController";
import { EditPartner } from "./components/EditPartner";
import { TablePage } from "../TablePage";
import { AddTableCard } from "../TablePage/components/AddTableCard";
import { Settings } from "lucide-react";
import { ConfigPartner } from "./components/ConfigPartner";
import { cn } from "@/app/utils/cn";

export default function PartnersPage() {
  const {
    productsConfig,
    isLoading,
    PERMISSIONS,
    selectedPartner,
    setSelectedPartner,
    selectedPartnerData,
    setSelectedPartnerData,
    methods,
    partnersConfig,
    isOpenConfigPartner,
    setIsOpenConfigPartner,
    selectedConfigProduct,
    setSelectedConfigProduct,
  } = usePartnersPageController();

  const { getProductLabel, getProductImage, getProductColor } =
    useProductsCatalog();
  const { isProductVisible } = useProductVisibility();

  if (isLoading) {
    return <Spinner />;
  }

  if (
    selectedPartner === PartnerType.FGTS ||
    selectedPartner === PartnerType.CLT
  ) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex w-full items-center justify-between">
          <ButtonBack
            label={`Produtos - ${getProductLabel(selectedPartner as PartnerType)}`}
            action={() => setSelectedPartner(null)}
          />
          {PERMISSIONS.tables.create && (
            <FormProvider {...methods}>
              <AddTableCard type={selectedPartner} />
            </FormProvider>
          )}
        </div>

        <TablePage type={selectedPartner} />
      </div>
    );
  }

  const getPartnerData = (partner: PartnerType) => {
    return {
      image: getProductImage(partner),
      color: getProductColor(partner),
      label: getProductLabel(partner),
    };
  };

  return (
    <div className="grid grid-cols-1 gap-4 pb-10 pt-4 md:grid-cols-2 lg:grid-cols-3">
      {Object.entries(productsConfig?.products || {})
        .filter(([partnerKey]) => isProductVisible(partnerKey))
        .map(([partnerKey, productDetail]) => {
          const partner = partnerKey as PartnerType;
          const config = partnersConfig[partner];

          if (!config) return null;
          const hasTablePage =
            partner === PartnerType.FGTS || partner === PartnerType.CLT;
          const itemsToShow = hasTablePage
            ? productDetail.items.slice(0, 3)
            : config.itemsToShow(productDetail.items);

          const isDisabled = productDetail.state === TriStateEnum.ON;

          return (
            <AlertDialog key={partner}>
              <AlertDialogTrigger asChild>
                <Card
                  className={cn(
                    "cursor-pointer p-6 transition-shadow hover:shadow-lg",
                    isDisabled &&
                      "border-transparent bg-gray-100 hover:shadow-none",
                  )}
                  onClick={() => {
                    if (hasTablePage) {
                      setSelectedPartner(partner);
                    } else {
                      setSelectedPartnerData({
                        partner,
                        itens: productDetail.items,
                        tableId: productDetail.tableId || productDetail._id,
                        delay: productDetail.delay,
                        isActive: productDetail.isActive,
                        state: productDetail.state,
                      });
                    }
                  }}
                >
                  <div
                    className="flex h-[110px] w-full items-center justify-center overflow-hidden rounded-lg"
                    style={{ backgroundColor: getPartnerData(partner).color }}
                  >
                    <img
                      alt="parceiro img"
                      src={getPartnerData(partner).image}
                      className="h-full w-auto object-contain"
                      style={{ aspectRatio: "2/1" }}
                    />
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <p className="font-bold">{getPartnerData(partner).label}</p>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedConfigProduct(productDetail);
                        setIsOpenConfigPartner(true);
                      }}
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-sm font-medium text-black03">
                      {config.title}
                    </p>
                    <p className="font-medium text-black03">
                      {productDetail.items.length}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-col gap-1">
                    {itemsToShow.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <p className="text-sm font-medium text-black04">
                          {config.renderLabel(item, index, itemsToShow)}
                        </p>
                        <div className="flex-1 border-t border-dashed border-black04"></div>
                        <p className="text-sm font-medium">
                          {config.renderValue(item)}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </AlertDialogTrigger>

              <EditPartner partnerData={selectedPartnerData} />
            </AlertDialog>
          );
        })}

      <ConfigPartner
        open={isOpenConfigPartner}
        onOpenChange={setIsOpenConfigPartner}
        product={selectedConfigProduct}
      />
    </div>
  );
}
